import { useQuery } from '@tanstack/react-query';
import {
  Building2,
  Users,
  FileText,
  TrendingUp,
  Activity,
  CheckCircle,
  Clock,
  Shield,
  ArrowRight,
  BarChart3,
  Globe,
} from 'lucide-react';
import { StatCard, Skeleton, Card, CardHeader } from '@azyntor-labs/nexus-ui';
import { tenantsApi, dashboardApi } from '@/api/endpoints';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { useTenantSelector } from '@/components/layout/SystemAdminLayout';
import { Tenant } from '@/types';

// Loading skeleton
const SystemAdminDashboardSkeleton = () => (
  <div className="space-y-6 animate-in fade-in duration-300">
    <div className="flex items-center justify-between">
      <div>
        <Skeleton width={250} height={28} className="rounded-lg" />
        <Skeleton width={320} height={16} className="mt-2 rounded" />
      </div>
    </div>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="glass-card p-4">
          <div className="flex items-center justify-between mb-3">
            <Skeleton width={80} height={12} className="rounded" />
            <Skeleton variant="circular" width={32} height={32} />
          </div>
          <Skeleton width={60} height={24} className="rounded-lg" />
        </div>
      ))}
    </div>
  </div>
);

export const SystemAdminDashboard = () => {
  const { selectedTenant } = useTenantSelector();

  // Fetch all tenants for platform stats
  const { data: tenantsResponse, isLoading: isLoadingTenants } = useQuery({
    queryKey: ['system-admin-tenants'],
    queryFn: () => tenantsApi.list(),
  });

  // Fetch admin dashboard (tenant-scoped if tenant selected)
  const { data: dashboardResponse, isLoading: isLoadingDashboard } = useQuery({
    queryKey: ['system-admin-dashboard', selectedTenant?.id],
    queryFn: () => dashboardApi.getAdminDashboard(selectedTenant?.id),
    enabled: !!selectedTenant, // Only fetch when tenant is selected
  });

  const tenants: Tenant[] = tenantsResponse?.data?.data || [];
  const dashboard = dashboardResponse?.data?.data;

  const activeTenants = tenants.filter((t) => t.is_active).length;
  const inactiveTenants = tenants.filter((t) => !t.is_active).length;

  const isLoading = isLoadingTenants || (selectedTenant && isLoadingDashboard);

  if (isLoading) {
    return <SystemAdminDashboardSkeleton />;
  }

  // Platform-wide view (no tenant selected)
  if (!selectedTenant) {
    return (
      <div className="space-y-6 fade-in">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white/90">System Admin Dashboard</h1>
            <p className="text-white/50 mt-1 text-sm">Platform-wide overview and management</p>
          </div>
          <div className="flex items-center space-x-2 px-3 py-1.5 rounded-lg bg-purple-500/10 border border-purple-500/20">
            <Shield className="w-4 h-4 text-purple-400" />
            <span className="text-sm text-purple-400">Platform View</span>
          </div>
        </div>

        {/* Platform Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            title="Total Tenants"
            value={tenants.length}
            icon={<Building2 />}
            color="purple"
          />
          <StatCard
            title="Active Tenants"
            value={activeTenants}
            icon={<CheckCircle />}
            color="green"
            trend="up"
            trendValue={tenants.length > 0 ? `${Math.round((activeTenants / tenants.length) * 100)}%` : undefined}
          />
          <StatCard
            title="Inactive Tenants"
            value={inactiveTenants}
            icon={<Clock />}
            color="orange"
          />
          <StatCard
            title="System Health"
            value="99.9%"
            icon={<Activity />}
            color="teal"
          />
        </div>

        {/* Info Card */}
        <Card className="border-purple-500/20 bg-purple-500/5">
          <div className="p-6">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 rounded-xl bg-purple-500/20 border border-purple-500/30 flex items-center justify-center flex-shrink-0">
                <Globe className="w-6 h-6 text-purple-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white/90 mb-2">Platform-Wide View</h3>
                <p className="text-white/60 text-sm mb-4">
                  You are currently viewing platform-wide statistics. To view tenant-specific data,
                  select a tenant from the dropdown in the header.
                </p>
                <Link
                  to="/system-admin/tenants"
                  className="inline-flex items-center space-x-2 text-purple-400 hover:text-purple-300 text-sm font-medium"
                >
                  <span>Manage Tenants</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </Card>

        {/* Tenants Grid */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white/90">Recent Tenants</h2>
            <Link
              to="/system-admin/tenants"
              className="text-sm text-purple-400 hover:text-purple-300 flex items-center space-x-1"
            >
              <span>View All</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {tenants.slice(0, 6).map((tenant) => (
              <Card key={tenant.id} className="hover:border-white/[0.12] transition-colors">
                <div className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-teal-500/20 to-cyan-500/20 border border-teal-500/30 flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-teal-400" />
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-white/90">{tenant.name}</h3>
                        <p className="text-xs text-white/50">{tenant.domain}</p>
                      </div>
                    </div>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      tenant.is_active
                        ? 'bg-emerald-500/20 text-emerald-400'
                        : 'bg-red-500/20 text-red-400'
                    }`}>
                      {tenant.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs text-white/40">
                    <span>Created {format(new Date(tenant.created_at), 'MMM dd, yyyy')}</span>
                    <div className="flex items-center space-x-2">
                      {tenant.settings?.features?.proctoring && (
                        <span className="px-1.5 py-0.5 bg-white/[0.04] rounded">Proctoring</span>
                      )}
                      {tenant.settings?.features?.analytics && (
                        <span className="px-1.5 py-0.5 bg-white/[0.04] rounded">Analytics</span>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>

        {/* Quick Actions */}
        <Card>
          <CardHeader
            title="Quick Actions"
            subtitle="Common system administration tasks"
            icon={<Shield className="w-4 h-4 text-purple-400" />}
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 p-4">
            <Link
              to="/system-admin/tenants"
              className="flex items-center space-x-3 p-3 rounded-lg bg-white/[0.02] border border-white/[0.04] hover:border-purple-500/30 hover:bg-purple-500/5 transition-all group"
            >
              <div className="w-9 h-9 bg-purple-500/20 rounded-lg flex items-center justify-center border border-purple-500/30">
                <Building2 className="w-4 h-4 text-purple-400" />
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-medium text-white/90">Manage Tenants</h4>
                <p className="text-xs text-white/40">Add or configure tenants</p>
              </div>
            </Link>

            <Link
              to="/system-admin/users"
              className="flex items-center space-x-3 p-3 rounded-lg bg-white/[0.02] border border-white/[0.04] hover:border-teal-500/30 hover:bg-teal-500/5 transition-all group"
            >
              <div className="w-9 h-9 bg-teal-500/20 rounded-lg flex items-center justify-center border border-teal-500/30">
                <Users className="w-4 h-4 text-teal-400" />
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-medium text-white/90">Manage Users</h4>
                <p className="text-xs text-white/40">Platform-wide user management</p>
              </div>
            </Link>

            <Link
              to="/system-admin/analytics"
              className="flex items-center space-x-3 p-3 rounded-lg bg-white/[0.02] border border-white/[0.04] hover:border-emerald-500/30 hover:bg-emerald-500/5 transition-all group"
            >
              <div className="w-9 h-9 bg-emerald-500/20 rounded-lg flex items-center justify-center border border-emerald-500/30">
                <BarChart3 className="w-4 h-4 text-emerald-400" />
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-medium text-white/90">View Analytics</h4>
                <p className="text-xs text-white/40">Platform statistics</p>
              </div>
            </Link>

            <Link
              to="/system-admin/activity"
              className="flex items-center space-x-3 p-3 rounded-lg bg-white/[0.02] border border-white/[0.04] hover:border-amber-500/30 hover:bg-amber-500/5 transition-all group"
            >
              <div className="w-9 h-9 bg-amber-500/20 rounded-lg flex items-center justify-center border border-amber-500/30">
                <Activity className="w-4 h-4 text-amber-400" />
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-medium text-white/90">Activity Logs</h4>
                <p className="text-xs text-white/40">System activity history</p>
              </div>
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  // Tenant-specific view
  const userStats = dashboard?.user_stats;
  const examStats = dashboard?.exam_stats;

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white/90">{selectedTenant.name}</h1>
          <p className="text-white/50 mt-1 text-sm">Tenant dashboard and statistics</p>
        </div>
        <div className="flex items-center space-x-2 px-3 py-1.5 rounded-lg bg-teal-500/10 border border-teal-500/20">
          <Building2 className="w-4 h-4 text-teal-400" />
          <span className="text-sm text-teal-400">Tenant View</span>
        </div>
      </div>

      {/* Tenant Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <StatCard
          title="Total Users"
          value={userStats?.total_users || 0}
          icon={<Users />}
          color="teal"
        />
        <StatCard
          title="Students"
          value={userStats?.total_students || 0}
          icon={<Users />}
          color="teal"
        />
        <StatCard
          title="Teachers"
          value={userStats?.total_teachers || 0}
          icon={<Users />}
          color="green"
        />
        <StatCard
          title="Total Exams"
          value={examStats?.total_exams || 0}
          icon={<FileText />}
          color="purple"
        />
        <StatCard
          title="Active Exams"
          value={examStats?.active_exams || 0}
          icon={<Activity />}
          color="orange"
        />
        <StatCard
          title="Pass Rate"
          value={`${(examStats?.average_pass_rate || 0).toFixed(0)}%`}
          icon={<TrendingUp />}
          color="blue"
        />
      </div>

      {/* Tenant Info */}
      <Card>
        <CardHeader
          title="Tenant Information"
          subtitle="Configuration and settings"
          icon={<Building2 className="w-4 h-4 text-teal-400" />}
        />
        <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <p className="text-xs text-white/40 uppercase tracking-wider mb-1">Domain</p>
            <p className="text-sm text-white/90">{selectedTenant.domain}</p>
          </div>
          <div>
            <p className="text-xs text-white/40 uppercase tracking-wider mb-1">Status</p>
            <span className={`inline-flex px-2 py-1 text-xs rounded-full ${
              selectedTenant.is_active
                ? 'bg-emerald-500/20 text-emerald-400'
                : 'bg-red-500/20 text-red-400'
            }`}>
              {selectedTenant.is_active ? 'Active' : 'Inactive'}
            </span>
          </div>
          <div>
            <p className="text-xs text-white/40 uppercase tracking-wider mb-1">Created</p>
            <p className="text-sm text-white/90">{format(new Date(selectedTenant.created_at), 'MMMM dd, yyyy')}</p>
          </div>
        </div>
        <div className="p-4 border-t border-white/[0.06]">
          <p className="text-xs text-white/40 uppercase tracking-wider mb-2">Enabled Features</p>
          <div className="flex flex-wrap gap-2">
            {selectedTenant.settings?.features?.proctoring && (
              <span className="px-2 py-1 text-xs bg-teal-500/20 text-teal-400 rounded-lg">Proctoring</span>
            )}
            {selectedTenant.settings?.features?.analytics && (
              <span className="px-2 py-1 text-xs bg-purple-500/20 text-purple-400 rounded-lg">Analytics</span>
            )}
            {selectedTenant.settings?.features?.question_banks && (
              <span className="px-2 py-1 text-xs bg-blue-500/20 text-blue-400 rounded-lg">Question Banks</span>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
};
